import { defineStore } from 'pinia';
import { authService, setAuthToken, clearAuthToken } from '@/services';

const TOKEN_KEY = 'authToken';

const getStoredToken = () => localStorage.getItem(TOKEN_KEY);
const setStoredToken = (token) => localStorage.setItem(TOKEN_KEY, token);
const clearStoredToken = () => localStorage.removeItem(TOKEN_KEY);

const INITIAL_AUTH_STATE = {
  isAuth: false,
  token: null,
  isLoading: false,
  error: null,
};

export const useUserStore = defineStore({
  id: 'user',
  state: () => {
    const storedToken = getStoredToken();
    if (storedToken) {
      setAuthToken(storedToken);
      return {
        isAuth: true,
        token: storedToken,
        isLoading: false,
        error: null,
      };
    }
    return { ...INITIAL_AUTH_STATE };
  },
  actions: {
    async login(credentials) {
      this.isLoading = true;
      this.error = null;

      try {
        const data = await authService.login(credentials);

        console.log('[UserStore] Login response:', data);

        if (data && data.token) {
          this.isAuth = true;
          this.token = data.token;
          setAuthToken(data.token);
          setStoredToken(data.token);
          console.log('[UserStore] Login successful, token set');
        } else {
          console.error('[UserStore] No token in response:', data);
          this.error = 'Kein Token vom Server erhalten.';
          throw new Error('No token received');
        }
      } catch (error) {
        // Handle specific error status codes
        if (error.status === 403) {
          this.error = error.message;
        } else if (error.status === 415) {
          this.error = 'Das eingegebene Passwort ist nicht korrekt.';
        } else if (error.status === 401) {
          this.error = 'Zu viele Anmeldeversuche. Bitte versuchen Sie es später erneut.';
        } else if (error.status === 204) {
          this.error = 'Server hat keine Antwort gesendet. Bitte überprüfen Sie die API-Konfiguration.';
        } else {
          this.error = error.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
        }
        console.error('[UserStore] Login error:', error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async autoLogin(ticketNumber, hash) {
      this.isLoading = true;
      this.error = null;

      try {
        const data = await authService.autoLogin({
          ticketNumber,
          hash,
        });

        if (data.token) {
          this.isAuth = true;
          this.token = data.token;
          setAuthToken(data.token);
          setStoredToken(data.token);
        }
      } catch (error) {
        this.error = 'Auto-Login fehlgeschlagen. Bitte versuchen Sie es erneut.';
        console.error('[UserStore] Auto-login error:', error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    logout() {
      this.isAuth = false;
      this.token = null;
      this.error = null;
      clearAuthToken();
      clearStoredToken();

      // Reset to initial state
      Object.assign(this, INITIAL_AUTH_STATE);
    },

    checkAuth() {
      return this.isAuth;
    },

    clearError() {
      this.error = null;
    },
  },

  getters: {
    isAuthenticated: (state) => state.isAuth && !!state.token,
    hasError: (state) => !!state.error,
  },
});
